import { FormikValues, getIn, useFormikContext } from 'formik';
import { Geocoding } from 'shared/geo/GeoHelpers';
import { useGeoPredictions } from 'shared/geo/GeoService';
import { applyGeocodedVenue } from 'shared/form/applyGeocodedVenue';
import { joinStrings } from 'shared/utils/StringUtils';

interface UseVenueAddressVerificationOptions {
  name: string;
  verifiedAddress: string;
  onVerify: (address: string) => void;
  isEnabled: boolean;
}

export function useVenueAddressVerification({
  name,
  verifiedAddress,
  onVerify,
  isEnabled,
}: UseVenueAddressVerificationOptions) {
  const {
    values,
    touched: formTouched,
    setValues,
  } = useFormikContext<FormikValues>();

  const currentAddress = (getIn(values, `${name}.address`) as string) || '';
  const fieldTouched = !!getIn(formTouched, `${name}.address`);
  const isVerified = !currentAddress || currentAddress === verifiedAddress;
  const needsSuggestion =
    fieldTouched && !isVerified && currentAddress.length > 0;

  const { data: predictions = [] } = useGeoPredictions(
    isEnabled && needsSuggestion ? currentAddress : undefined,
    ['address'],
  );
  const topSuggestion = predictions[0] ?? null;

  const isPending = isEnabled && needsSuggestion && !!topSuggestion;

  function applySuggestion(geocoding: Geocoding) {
    onVerify(joinStrings(' ', geocoding.house_number, geocoding.address));
    void setValues((values) => applyGeocodedVenue(values, name, geocoding));
  }

  function dismissSuggestion() {
    onVerify(currentAddress);
  }

  return {
    isPending,
    topSuggestion,
    applySuggestion,
    dismissSuggestion,
  };
}
